import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check, Share2, Users, MessageCircle, Facebook } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.9 2H22l-7.6 8.7L23 22h-6.8l-5.3-6.9L4.8 22H1.7l8.1-9.3L1 2h7l4.8 6.3L18.9 2zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20z" />
  </svg>
);

export const InviteDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) api.get("/me/invite").then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [open]);

  const link = data ? `${window.location.origin}/join/${data.code}` : "";
  const msg = t("invite.message");
  const enc = encodeURIComponent;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch { /* clipboard blocked */ }
    setCopied(true); toast.success(t("invite.copied"));
    setTimeout(() => setCopied(false), 2000);
  };
  const nativeShare = () => navigator.share({ title: "NeTimeTV", text: msg, url: link }).catch(() => {});

  const channels = [
    { id: "whatsapp", label: "WhatsApp", icon: <MessageCircle size={16} />, cls: "bg-[#25D366] text-black", href: `https://wa.me/?text=${enc(`${msg} ${link}`)}` },
    { id: "facebook", label: "Facebook", icon: <Facebook size={16} />, cls: "bg-[#1877F2] text-white", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}` },
    { id: "x", label: "X", icon: <XIcon />, cls: "bg-white text-black", href: `https://twitter.com/intent/tweet?text=${enc(msg)}&url=${enc(link)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-dialog" className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl font-extrabold">{t("invite.title")}</DialogTitle>
          <DialogDescription className="text-zinc-400">{t("invite.body")}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 p-1.5 pl-3">
          <span data-testid="invite-link" className="flex-1 truncate text-sm text-zinc-200">{link || "…"}</span>
          <button data-testid="invite-copy-btn" onClick={copy} disabled={!link}
            className="shrink-0 inline-flex items-center gap-1.5 bg-brand hover:bg-red-600 text-white text-sm font-bold px-3.5 py-2 rounded-lg transition-colors disabled:opacity-50">
            {copied ? <Check size={15} /> : <Copy size={15} />} {t("invite.copy")}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {channels.map((c) => (
            <a key={c.id} href={link ? c.href : undefined} target="_blank" rel="noopener noreferrer" data-testid={`invite-share-${c.id}`}
              className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold hover:brightness-110 transition ${c.cls}`}>
              {c.icon} {c.label}
            </a>
          ))}
        </div>
        {typeof navigator !== "undefined" && navigator.share && (
          <button data-testid="invite-native-share-btn" onClick={nativeShare} disabled={!link}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold transition-colors">
            <Share2 size={16} /> {t("invite.share")}
          </button>
        )}

        <div data-testid="invite-joined-count" className="flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
          <Users size={16} /> {t("invite.joined", { count: data?.joined ?? 0 })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
